/**
 * Aspirations & Todos logic. PURE — no React.
 *
 * Storage: `my-hub:todos:items`, DAILY (reset at local midnight).
 * This list is deliberately unrelated to the top bar's One Priority
 * (`my-hub:top:priority`, Mayor-owned) — no shared key, no shared state.
 */
import { asRecord, asTrimmedString, type Todo } from './types';

export const MAX_TODO_TEXT = 80;

export function createTodo(id: string, text: string): Todo {
  return { id, text: text.trim().slice(0, MAX_TODO_TEXT), done: false };
}

export function toggleTodo(items: readonly Todo[], id: string): readonly Todo[] {
  return items.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
}

export function removeTodo(items: readonly Todo[], id: string): readonly Todo[] {
  return items.filter((t) => t.id !== id);
}

export function clearCompleted(items: readonly Todo[]): readonly Todo[] {
  return items.filter((t) => !t.done);
}

export interface TodoCounts {
  readonly done: number;
  readonly total: number;
}

export function todoCounts(items: readonly Todo[]): TodoCounts {
  return { done: items.reduce((n, t) => (t.done ? n + 1 : n), 0), total: items.length };
}

/**
 * Salvaging migration for the stored todo list. PURE. A row without text is
 * unrenderable and dropped; everything else is kept, so one bad row does not
 * cost the user the rest of the day's list.
 */
export function migrateTodos(raw: unknown): readonly Todo[] {
  if (!Array.isArray(raw)) return [];
  const out: Todo[] = [];
  const seen = new Set<string>();
  raw.forEach((row, index) => {
    const r = asRecord(row);
    if (r === null) return;
    const text = asTrimmedString(r.text).slice(0, MAX_TODO_TEXT);
    if (text === '') return;
    const id = asTrimmedString(r.id) || `todo-legacy-${index}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ id, text, done: r.done === true });
  });
  return out;
}
